import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';
import { apiDriverApp } from '../../../../libs/apiDriverApp';

const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return Promise.reject("No file selected");

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const arrayBuffer = e.target.result;
                const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                const requiredHeaders = [
                    "Email",
                    "FirstName",
                    "Job",
                    "LastName",
                    "MobilePhone",
                    "PinCode",
                    "Username",
                    "Device"
                ];

                const headers = jsonData[0];

                const missingHeaders = requiredHeaders.filter(
                    (header) => !headers.includes(header)
                );

                if (missingHeaders.length > 0) {
                    reject(new Error(`The file uploaded is missing headers: ${missingHeaders.join(", ")}`));
                    return;
                }

                const invalidHeaders = headers.filter(
                    (header) => !requiredHeaders.includes(header)
                );

                if (invalidHeaders.length > 0) {
                    reject(new Error(`The file uploaded contains invalid headers: ${invalidHeaders.join(", ")}`));
                    return;
                }

                const excelData = jsonData.slice(1).map((row) => {
                    const obj = {};
                    headers.forEach((header, index) => {
                        obj[header] = row[index] || null;
                    });
                    return obj;
                });

                resolve(excelData);
            } catch (error) {
                console.error("Error processing the file:", error);
                reject(error);
            }
        };

        reader.readAsArrayBuffer(file);
    });
};

export const downloadAppVersionReport = async () => {
    try {
        const { data } = await apiDriverApp.get('/api/hr/employees/app-version-report/');

        if (!data || data.length === 0) {
            Swal.fire('No Data', 'No driver app version data found.', 'info');
            return;
        }

        const formattedData = data.map(driver => ({
            Username: driver.username || '',
            "First Name": driver.first_name || '',
            "Last Name": driver.last_name || '',
            "Last Login": driver.last_login ? new Date(driver.last_login).toLocaleString() : 'Never',
            "App Version": driver.app_version || 'Unknown',
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "App Version Report");

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const excelBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        saveAs(excelBlob, "driver_app_version_report.xlsx");
    } catch (error) {
        console.error("Error downloading app version report:", error);
        Swal.fire('Error', 'Failed to download the app version report.', 'error');
    }
};

export const downloadDriverList = async () => {
    try {
        // Fetch employees and last status changes in parallel
        const [employeesResponse, statusChangesResponse] = await Promise.all([
            apiDriverApp.get('/api/hr/employees/'),
            apiDriverApp.get('/api/trips/last-status-changes/').catch(err => {
                console.error('Failed to fetch last status changes:', err);
                return { data: {} };
            })
        ]);

        const data = employeesResponse.data;
        const lastStatusChanges = statusChangesResponse.data || {};

        if (!data || data.length === 0) {
            console.warn("No hay datos para exportar.");
            return;
        }

        // Filtra y organiza los datos para el Excel
        const formattedData = data.map(driver => {
            const username = driver.user.username || '';
            const lastStatus = lastStatusChanges[username];

            return {
                Username: username,
                // PinCode: driver.pin_code || '',
                Email: driver.user.email || '',
                FirstName: driver.user.first_name || '',
                LastName: driver.user.last_name || '',
                Job: driver.job ? driver.job.name : '',
                MobilePhone: driver.mobile_phone || '',
                "Calendar Name": (driver.resource && driver.resource.calendar) ? driver.resource.calendar.name : '',
                "Rotation Number": (driver.resource && driver.resource.calendar) && !isNaN(driver.resource.rotation_position) ? driver.resource.rotation_position + 1 : '',
                "Max Rotations": (driver.resource && driver.resource.calendar) ? driver.resource.calendar.rotation_weeks : '',
                "Last Status Update": lastStatus ? new Date(lastStatus.changed_at).toLocaleString() : 'Never',
                "Last Status": lastStatus ? lastStatus.status : 'N/A',
                "Last Trip ID": lastStatus ? lastStatus.trip_id : '',
            };
        });

        // Crea la hoja de trabajo (worksheet) y el libro de trabajo (workbook)
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Drivers");

        // Genera el archivo y lo descarga
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const excelBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        saveAs(excelBlob, "driver_list.xlsx");
    } catch (error) {
        console.error("Error descargando la lista de conductores:", error);
    }
};


const validateRequiredFields = (data, requiredFields) => {
    const invalidRecords = [];

    data.forEach((item, index) => {
        const isValid = requiredFields.every(field => item[field] && String(item[field]).trim() !== '');
        if (!isValid) {
            invalidRecords.push(`Row ${index + 1}`);
        }
    });

    return invalidRecords;
};

const validateMatchingRecords = (data, dbMap) => {
    const matchingRecords = [];

    data.forEach((item, index) => {
        const usernameKey = String(item.Username || "").trim().toLowerCase();
        const emailKey = String(item.Email || "").trim().toLowerCase();

        if (dbMap.has(usernameKey) || [...dbMap.values()].includes(emailKey)) {
            matchingRecords.push(`Row ${index + 1}`);
        }
    });

    return matchingRecords;
};

const createNewDrivers = async (dbMap, uploadedData) => {

    const requests = uploadedData.map(async (item) => {
        const match = dbMap.get(String(item.Username));

        if (!match) {
            const body = {
                username: item.Username,
                pin_code: item.PinCode,
                email: item.Email || 'no@email.com',
                first_name: item.FirstName,
                last_name: item.LastName,
                job: item.Job,
                work_email: item.Email || 'no@email.com',
                work_phone: item.MobilePhone,
                mobile_phone: item.MobilePhone,
            };

            if (item.Device) {
                body.device_type = item.Device
            }
            try {
                await apiDriverApp.post('api/hr/employees/', body);
                return { username: item.Username, success: true };
            } catch (error) {
                console.error(`Error processing employee ${item.Username}:`, error.response);
                return { username: item.Username, error: error.message };
            }
        } else {
            console.log(`This driver already exists: ${item.Username}`);
            return { username: item.Username, success: false, message: 'Already exists' };
        }
    });

    const results = await Promise.all(requests);
    const failed = results.filter(res => res.error);

    if (failed.length > 0) {
        throw new Error(`Could not create: ${failed.map(e => e.username).join(', ')}`);
    }

    return results;
};

export const onFileUpload = async (event) => {
    try {
        const loadingAlert = Swal.fire({
            title: 'Uploading...',
            text: 'Please wait while the file is being uploaded and processed.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            onBeforeOpen: () => {
                Swal.showLoading();
            },
        });
        const results = await handleFileChange(event);

        const invalidRecords = validateRequiredFields(results, ['Username', 'PinCode', 'Job']);
        if (invalidRecords.length > 0) {
            Swal.fire(
                'Validation Error',
                `The following records are missing required fields (Username or PinCode):\n${invalidRecords.join(', ')}`,
                'error'
            );
            return;
        }

        const { data: dbDrivers } = await apiDriverApp.get('/api/hr/employees/');
        const dbMap = new Map(
            dbDrivers.map(item => [String(item.user.username).trim().toLowerCase(), String(item.user.email).trim().toLowerCase()])
        );

        const existingRecords = validateMatchingRecords(results, dbMap)
        if (existingRecords.length > 0) {
            await Swal.fire(
                'Validation Error',
                `The following records already exist in the database:\n${existingRecords.join(', ')}`,
                'error'
            );
            return;
        }

        await createNewDrivers(dbMap, results);
        event.target.value = "";
        Swal.close();
        Swal.fire('Success', 'The file was uploaded successfully', 'success');

    } catch (error) {
        console.error("Error during file upload:", error);
        Swal.close();
        if (error.response) {
            Swal.fire('Error', error.response.data.detail || 'An error occurred', 'error');
        } else if (error.request) {
            Swal.fire('Network Error', 'No response from server. Please try again later.', 'error');
        } else {
            Swal.fire('Error', error.message || 'An unexpected error occurred', 'error');
        }
        event.target.value = "";
    }
};

export const getFullName = (firstName = 'no', lastName = 'data') => {
    const format1 = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    const format2 = lastName.charAt(0).toUpperCase() + lastName.slice(1);
    return `${format1} ${format2}`
}

const getTotalDays = (from_date, to_date) => {
    const fromDate = new Date(from_date);
    const toDate = new Date(to_date);
    const diffTime = toDate - fromDate;
    const totalDays = diffTime / (1000 * 60 * 60 * 24) + 1;
    return totalDays
}

export const fetchTimeOffRequests4Report = async (firstDay, lastDay) => {
    try {
        const { data } = await apiDriverApp.get(`api/hr/leave/?from_date=${firstDay}&to_date=${lastDay}`)
        const formattedData = data.map(request => ({
            "Employee#": request.employee.user.username || '',
            Name: `${request.employee.user.first_name} ${request.employee.user.last_name}` || '',
            "Driver Station": request.employee && request.employee.station
                ? request.employee.station.code
                : '',
            Schedule: request.employee && request.employee.resource && request.employee.resource.calendar
                ? request.employee.resource.calendar.name
                : '',
            "Leave Type": request.leave_type.name || '',
            "Start Date": request.from_date,
            "End Date": request.to_date,
            Days: getTotalDays(request.from_date, request.to_date),
            Status: request.status || '',
            "Status Update": `${request.latest_status.date_changed.slice(0, 16).replace("T", " ")} ${request.latest_status.changed_by}`
        }));
        return formattedData
    } catch (error) {
        console.log(error)
        throw new Error('No data found');
    }
}

export const fetchIncidents4Report = async (firstDay, lastDay) => {
    try {
        const { data } = await apiDriverApp.get(`api/hr/shift-incidents/?from_date=${firstDay}&to_date=${lastDay}`)
        const formattedData = data.map(request => ({
            "Driver number": request.employee.user.username || '',
            "Employee Name": `${request.employee.user.first_name} ${request.employee.user.last_name}` || '',
            "Driver Station": request.employee && request.employee.station
                ? request.employee.station.code
                : '',
            "Incident Type": request.incident_type_display || '',
            "Date": request.date,
            "Minutes Late": request.minutes_late,
            "Notes": request.notes,
            "Reported by": request.reported_by
        }));
        return formattedData
    } catch (error) {
        console.log(error)
        throw new Error('No data found');
    }
}

export const getAllDrivers = async () => {
    try {
        const { data } = await apiDriverApp.get('api/hr/employees/');
        return data
    } catch (error) {
        console.log(error);
        throw error
    }
}

export const getShiftSwaps = async (params) => {
    try {
        let url = 'api/hr/shift-swaps/';

        if (params && Object.keys(params).length > 0) {
            const queryString = Object.entries(params)
                .map(([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                )
                .join('&');
            url += `?${queryString}`;
        }

        const { data } = await apiDriverApp(url);
        return data;
    } catch (error) {
        console.log(error);
    }
};

export const getAdditionalWorkDays = async (params) => {
    try {
        let url = 'api/hr/add-workdays/';

        if (params && Object.keys(params).length > 0) {
            const queryString = Object.entries(params)
                .map(([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                )
                .join('&');
            url += `?${queryString}`;
        }

        const { data } = await apiDriverApp(url)
        return data
    } catch (error) {
        console.log(error)
    }
}

export const getStatusStyle = (status) => {
    switch (status) {
        case "sent":
            return { text: "Pending", backgroundColor: "#ffc107" };
        case "pending":
            return { text: "Pending", backgroundColor: "gray" };
        case "approved":
            return { text: "Approved", backgroundColor: "#28A745" };
        case "denied":
            return { text: "Denied", backgroundColor: "#DC3545" };
        case "canceled":
            return { text: "Canceled", backgroundColor: "#DC3545" };
        case "requested":
            return { text: "Canceled", backgroundColor: "gray" };
        case "assigned":
            return { text: "Canceled", backgroundColor: "#ffc107" };
        case "completed":
            return { text: "Completed", backgroundColor: "blue" };

        default:
            return { text: "Unknown", backgroundColor: "white" };
    }
};

export const getSpecificDriverShifts = async (params) => {
   try {
      let url = 'api/hr/custom-driver-shifts/';

      if (params && Object.keys(params).length > 0) {
         const queryString = Object.entries(params)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
         url += `?${queryString}`;
      }

      const { data } = await apiDriverApp(url);
      return data;
   } catch (error) {
      console.log(error);
   }
};

export const getWeekDayName = (dayofweek) => {
   const days = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
   return days[dayofweek] || '';
};

const isNullOrUndefined = (value) => value === null || value === undefined;

export function getRotatedWeekNumber(realWeekIndex, rotationPosition, rotationWeeks) {
   if (isNullOrUndefined(realWeekIndex) || isNullOrUndefined(rotationPosition) || isNullOrUndefined(rotationWeeks)) {
      return 0;
   }
   if (rotationWeeks <= 0) return 1; // security fallback
   // Applies the inverse rotation (shift backwards)
   const visibleIndex = (realWeekIndex - rotationPosition + rotationWeeks) % rotationWeeks;
   return visibleIndex;
}

export function getUnrotatedWeekNumber(visibleWeekIndex, rotationPosition, rotationWeeks) {
   if (isNullOrUndefined(visibleWeekIndex) || isNullOrUndefined(rotationPosition) || isNullOrUndefined(rotationWeeks)) {
      return 0;
   }
   if (rotationWeeks <= 0) return 1; // security fallback
   // Applies the inverse rotation (shift forwards)
   const realIndex = (visibleWeekIndex + rotationPosition) % rotationWeeks;
   return realIndex;
}
